import React, { useEffect } from 'react';

/**
 * CleverTap Web SDK Initialization Component
 * Initializes CleverTap for web tracking
 * 
 * @param accountId - CleverTap Account ID (e.g., "TEST-KK4-W7K-R67Z")
 * @param onLoad - Optional callback when CleverTap is loaded
 */
interface CleverTapProps {
  accountId?: string;
  onLoad?: () => void;
}

const CleverTap: React.FC<CleverTapProps> = ({ 
  accountId = process.env.REACT_APP_CLEVERTAP_PROJECT_ID || 'RK4-W7K-R67Z',
  onLoad 
}) => {
  useEffect(() => {
    // Check if CleverTap is already initialized
    if ((window as any).clevertap) {
      console.log('[CleverTap] Already initialized');
      if (onLoad) {
        onLoad();
      }
      return;
    }

    // Initialize CleverTap object
    (window as any).clevertap = {
      event: [],
      profile: [],
      account: [],
      onUserLogin: [],
      notifications: [],
      privacy: []
    };

    // Set account ID
    (window as any).clevertap.account.push({ id: accountId });
    (window as any).clevertap.privacy.push({ optOut: false });
    (window as any).clevertap.privacy.push({ useIP: false });

    // Load CleverTap script
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.id = 'clevertap-script';
    script.async = true;
    script.src = (document.location.protocol === 'https:' 
      ? 'https://d2r1yp2w7bby2u.cloudfront.net' 
      : 'http://static.clevertap.com') + '/js/clevertap.min.js';
    
    script.onload = () => {
      console.log('[CleverTap] Script loaded successfully');
      if (onLoad) {
        onLoad();
      }
    };

    script.onerror = () => {
      console.error('[CleverTap] Failed to load script');
    };

    // Insert script into body tag (as required by CleverTap)
    let bodyCheckInterval: NodeJS.Timeout | null = null;
    let fallbackTimeout: NodeJS.Timeout | null = null;
    
    if (document.body) {
      document.body.appendChild(script);
    } else {
      // If body doesn't exist yet, wait for DOM to be ready
      bodyCheckInterval = setInterval(() => {
        if (document.body) {
          if (bodyCheckInterval) clearInterval(bodyCheckInterval);
          document.body.appendChild(script);
        }
      }, 10);
      
      // Fallback: append to head if body never loads (shouldn't happen)
      fallbackTimeout = setTimeout(() => {
        if (!script.parentNode) {
          if (bodyCheckInterval) clearInterval(bodyCheckInterval);
          document.head.appendChild(script);
        }
      }, 1000);
    }

    return () => {
      // Cleanup intervals/timeouts if component unmounts
      if (bodyCheckInterval) {
        clearInterval(bodyCheckInterval);
      }
      if (fallbackTimeout) {
        clearTimeout(fallbackTimeout);
      }
    };
  }, [accountId, onLoad]);

  return null;
};

export default CleverTap;
